/**
 * 안전보건인력 (SAP_BASIC_01) 전용 로직
 * other 케이스에서 사용
 */

const FONT_FAMILY = '"Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", sans-serif';

// 조직도 데이터를 파싱하여 departments, members, lines 구조로 변환
const ParseOrganizationData = (documentData) => {
  if (!documentData) return null;

  const orgDataRaw =
    documentData?.orgData ??
    documentData?.oraData ??
    documentData?.ORG_DATA ??
    documentData?.ORA_DATA ??
    null;

  if (!orgDataRaw) return null;

  let parsedData = {};
  if (typeof orgDataRaw === 'string') {
    try {
      parsedData = JSON.parse(orgDataRaw);
    } catch (e) {
      console.error('조직도 데이터 파싱 오류:', e);
      return null;
    }
  } else if (typeof orgDataRaw === 'object') {
    parsedData = orgDataRaw;
  }

  const departments = (parsedData.departments || []).map((dept, idx) => ({
    id: dept.id || dept.depCd || `dept-${idx}`,
    depCd: dept.depCd || dept.id,
    depNmLast:
      dept.depNmLast || dept.name || dept.depNm1 || dept.DEP_NM || '부서',
    name: dept.name || dept.depNmLast || dept.depNm1 || dept.DEP_NM || '부서',
    x: typeof dept.x === 'number' ? dept.x : null,
    y: typeof dept.y === 'number' ? dept.y : null,
  }));

  const allMembers = (parsedData.members || []).concat(
    parsedData.manualMembers || []
  );
  const members = allMembers.map((m, idx) => ({
    id: m.id || `member-${idx}`,
    departmentId: m.departmentId || m.depCd,
    name: m.NAME || m.name || '',
    position: m.POSITION || m.position || '',
  }));

  const lines = (parsedData.lines || []).map((line, idx) => ({
    id: line.id || `line-${idx}`,
    sourceId: line.sourceId || line.source,
    targetId: line.targetId || line.target,
    startAnchor: line.startAnchor || 'auto',
    endAnchor: line.endAnchor || 'auto',
    path: line.path || 'straight',
    curveness: line.curveness || 0.5,
    strokeWidth: line.strokeWidth || 2,
    strokeColor: line.strokeColor || '#333',
  }));

  return { departments, members, lines };
};

// Canvas에 둥근 사각형 그리기 (roundRect 미지원 브라우저 대응)
const DrawRoundRect = (ctx, x, y, w, h, radii) => {
  const r =
    typeof radii === 'number'
      ? [radii, radii, radii, radii]
      : Array.isArray(radii)
        ? radii.length === 4
          ? radii
          : [radii[0] || 0, radii[1] || 0, radii[0] || 0, radii[1] || 0]
        : [0, 0, 0, 0];

  const [tl, tr, br, bl] = r;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
  ctx.lineTo(x + bl, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
  ctx.lineTo(x, y + tl);
  ctx.quadraticCurveTo(x, y, x + tl, y);
  ctx.closePath();
};

// 앵커 포인트 좌표 계산
const GetAnchorPoint = (pos, size, anchor) => {
  switch (anchor) {
    case 'left':
      return { x: pos.x, y: pos.y + size.height / 2 };
    case 'right':
      return { x: pos.x + size.width, y: pos.y + size.height / 2 };
    case 'top':
      return { x: pos.x + size.width / 2, y: pos.y };
    case 'bottom':
      return { x: pos.x + size.width / 2, y: pos.y + size.height };
    default:
      return { x: pos.x + size.width / 2, y: pos.y + size.height };
  }
};

// 조직도 이미지 생성 (Canvas API 기반)
export const PrintLogic1 = async (documentData) => {
  const orgData = ParseOrganizationData(documentData);
  if (!orgData || orgData.departments.length === 0) return null;

  const { departments, members, lines } = orgData;

  const membersByDept = {};
  departments.forEach((dept) => {
    membersByDept[dept.id] = members.filter(
      (m) => m.departmentId === dept.id || m.departmentId === dept.depCd
    );
  });

  const CARD_WIDTH = 220;
  const CARD_PADDING = 12;
  const HEADER_HEIGHT = 36;
  const MEMBER_ROW_HEIGHT = 28;
  const MEMBER_GAP = 4;
  const DEPT_GAP_X = 60;
  const DEPT_GAP_Y = 50;
  const CANVAS_MARGIN = 40;

  const deptSizes = {};
  departments.forEach((dept) => {
    const memberCount = (membersByDept[dept.id] || []).length;
    const contentHeight =
      Math.max(memberCount, 1) * (MEMBER_ROW_HEIGHT + MEMBER_GAP) -
      MEMBER_GAP;
    const height = HEADER_HEIGHT + CARD_PADDING * 2 + contentHeight;
    deptSizes[dept.id] = { width: CARD_WIDTH, height };
  });

  const deptPositions = {};
  const hasPositions = departments.some(
    (d) => d.x !== null && d.y !== null
  );

  if (hasPositions) {
    departments.forEach((dept) => {
      deptPositions[dept.id] = { x: dept.x ?? 0, y: dept.y ?? 0 };
    });
  } else {
    const cols = Math.max(1, Math.ceil(Math.sqrt(departments.length)));
    let currentY = CANVAS_MARGIN;
    for (let row = 0; row < Math.ceil(departments.length / cols); row++) {
      let maxRowHeight = 0;
      for (let col = 0; col < cols; col++) {
        const idx = row * cols + col;
        if (idx >= departments.length) break;
        const dept = departments[idx];
        deptPositions[dept.id] = {
          x: CANVAS_MARGIN + col * (CARD_WIDTH + DEPT_GAP_X),
          y: currentY,
        };
        maxRowHeight = Math.max(maxRowHeight, deptSizes[dept.id].height);
      }
      currentY += maxRowHeight + DEPT_GAP_Y;
    }
  }

  let maxX = 0;
  let maxY = 0;
  departments.forEach((dept) => {
    const pos = deptPositions[dept.id];
    const size = deptSizes[dept.id];
    maxX = Math.max(maxX, pos.x + size.width);
    maxY = Math.max(maxY, pos.y + size.height);
  });

  const canvasWidth = maxX + CANVAS_MARGIN;
  const canvasHeight = maxY + CANVAS_MARGIN;
  const scale = 2;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth * scale;
  canvas.height = canvasHeight * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  // 배경
  ctx.fillStyle = '#fafafa';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // 그리드 배경
  ctx.strokeStyle = '#e0e0e0';
  ctx.lineWidth = 0.5;
  for (let x = 0; x < canvasWidth; x += 20) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvasHeight);
    ctx.stroke();
  }
  for (let y = 0; y < canvasHeight; y += 20) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(canvasWidth, y);
    ctx.stroke();
  }

  // 연결선 그리기
  lines.forEach((line) => {
    const sourcePos = deptPositions[line.sourceId];
    const targetPos = deptPositions[line.targetId];
    if (!sourcePos || !targetPos) return;

    const sourceSize = deptSizes[line.sourceId];
    const targetSize = deptSizes[line.targetId];
    if (!sourceSize || !targetSize) return;

    const start = GetAnchorPoint(sourcePos, sourceSize, line.startAnchor);
    const end = GetAnchorPoint(targetPos, targetSize, line.endAnchor);

    ctx.strokeStyle = line.strokeColor || '#333';
    ctx.lineWidth = line.strokeWidth || 2;
    ctx.beginPath();

    if (line.path === 'smooth' && line.curveness) {
      const cp1X = start.x + (end.x - start.x) * line.curveness;
      const cp2X = end.x - (end.x - start.x) * line.curveness;
      ctx.moveTo(start.x, start.y);
      ctx.bezierCurveTo(cp1X, start.y, cp2X, end.y, end.x, end.y);
    } else if (line.path === 'grid') {
      const midX = (start.x + end.x) / 2;
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(midX, start.y);
      ctx.lineTo(midX, end.y);
      ctx.lineTo(end.x, end.y);
    } else {
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
    }

    ctx.stroke();
  });

  // 부서 카드 그리기
  departments.forEach((dept) => {
    const pos = deptPositions[dept.id];
    const size = deptSizes[dept.id];
    const deptMembers = membersByDept[dept.id] || [];

    // 그림자
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    // 카드 배경
    ctx.fillStyle = '#ffffff';
    DrawRoundRect(ctx, pos.x, pos.y, size.width, size.height, 8);
    ctx.fill();
    ctx.restore();

    // 카드 테두리
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    DrawRoundRect(ctx, pos.x, pos.y, size.width, size.height, 8);
    ctx.stroke();

    // 헤더 배경
    ctx.fillStyle = '#1976d2';
    DrawRoundRect(ctx, pos.x, pos.y, size.width, HEADER_HEIGHT, [8, 8, 0, 0]);
    ctx.fill();

    // 부서명
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 13px ${FONT_FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      dept.depNmLast || dept.name,
      pos.x + size.width / 2,
      pos.y + HEADER_HEIGHT / 2
    );

    // 인력 목록
    let memberY = pos.y + HEADER_HEIGHT + CARD_PADDING;

    if (deptMembers.length === 0) {
      ctx.fillStyle = '#9e9e9e';
      ctx.font = `12px ${FONT_FAMILY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        '인력 없음',
        pos.x + size.width / 2,
        memberY + MEMBER_ROW_HEIGHT / 2
      );
    } else {
      deptMembers.forEach((member) => {
        ctx.fillStyle = '#f5f5f5';
        DrawRoundRect(
          ctx,
          pos.x + CARD_PADDING,
          memberY,
          size.width - CARD_PADDING * 2,
          MEMBER_ROW_HEIGHT,
          4
        );
        ctx.fill();

        ctx.fillStyle = '#212121';
        ctx.font = `13px ${FONT_FAMILY}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        const memberText = member.position
          ? `${member.name} (${member.position})`
          : member.name;
        ctx.fillText(
          memberText,
          pos.x + CARD_PADDING + 8,
          memberY + MEMBER_ROW_HEIGHT / 2
        );

        memberY += MEMBER_ROW_HEIGHT + MEMBER_GAP;
      });
    }
  });

  const imageDataUrl = canvas.toDataURL('image/png');
  const res = await fetch(imageDataUrl);
  return res.blob();
};
